import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, PhoneCall, Truck, Zap, BarChart2, Layers, ShoppingBag, CheckCircle, UserPlus, ArrowRight, Menu, X, LogIn, ChevronRight } from 'lucide-react';
import { APP_ROUTES } from '../utils/auth';

// ─── Config ───────────────────────────────────────────────────────────────────
const WA_NUMBER  = '221769040289';
const WA_MESSAGE = encodeURIComponent('Bonjour Wakaana Express ! Je suis e-commerçant et je voudrais en savoir plus sur vos services.');
const WA_LINK    = `https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`;
const F          = "'Alan Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─── Icône WhatsApp SVG ───────────────────────────────────────────────────────
const WaIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.526 5.847L.057 23.882l6.197-1.625A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.817 9.817 0 01-5.006-1.371l-.36-.213-3.671.963.98-3.588-.234-.369A9.818 9.818 0 0112 2.182c5.427 0 9.818 4.391 9.818 9.818 0 5.428-4.391 9.818-9.818 9.818z"/>
  </svg>
);

// ─── Données ──────────────────────────────────────────────────────────────────
const SERVICES = [
  {
    Icon: Package,
    title: 'Gestion de stock',
    desc: 'On stocke vos produits et on suit vos entrées/sorties en temps réel. Plus jamais de rupture non détectée.',
    tags: ['Stockage sécurisé', 'Inventaire en temps réel', 'Alertes de rupture'],
    img: '/images/equipe-entrepot.webp',
    imgAlt: 'Équipe Wakaana Express préparant les colis en entrepôt',
    imgPos: 'center 40%',
  },
  {
    Icon: PhoneCall,
    title: 'Closing client',
    desc: 'Notre équipe appelle vos clients, confirme chaque commande et réduit vos taux d\'annulation. Vous vendez, on confirme.',
    tags: ['Appels de confirmation', 'Suivi des rappels', 'Taux de livraison optimisé'],
    img: '/images/marchand-bureau.webp',
    imgAlt: 'Marchand suivant ses commandes depuis son téléphone',
    imgPos: 'center 35%',
  },
  {
    Icon: Truck,
    title: 'Livraison express',
    desc: 'Vos commandes confirmées sont prises en charge et livrées partout au Sénégal avec un suivi GPS en temps réel.',
    tags: ['Livraison J+1', 'Suivi GPS live', 'Partout au Sénégal'],
    img: '/images/livraison-client.webp',
    imgAlt: 'Livreur Wakaana Express remettant un colis à une cliente',
    imgPos: 'center 30%',
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Vous créez votre boutique',
    desc: 'Inscrivez-vous, ajoutez vos produits et configurez votre espace marchand en quelques minutes.',
  },
  {
    num: '02',
    title: 'Vos commandes arrivent',
    desc: 'Importez depuis Shopify, Google Sheets ou saisissez manuellement. Tout est centralisé.',
  },
  {
    num: '03',
    title: 'On s\'occupe du reste',
    desc: 'Stock, appels de confirmation, expédition et livraison — notre équipe gère tout pour vous.',
  },
];

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar({ onLogin, onSignup }) {
  const [open, setOpen] = useState(false);

  const navLinks = [
    { label: 'Nos services',        href: '#services' },
    { label: 'Comment ça marche',   href: '#comment' },
    { label: 'Tarifs',              href: '#tarifs' },
    { label: 'Recrutement',         href: '/recrutement' },
  ];

  return (
    <>
      <style>{`
        @keyframes slideInMenu { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .nav-root {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          background: linear-gradient(90deg, #07162d 0%, #112c52 100%);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 2px 16px rgba(0,0,0,0.3);
          height: 60px;
          display: flex; align-items: center;
          justify-content: space-between;
          padding: 0 12px;
        }
        .nav-icon-btn {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.08);
          border: none; cursor: pointer; color: white;
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: background 0.15s;
        }
        .nav-icon-btn:hover { background: rgba(255,255,255,0.15); }

        /* Drawer */
        .nav-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(4px);
        }
        .nav-drawer {
          position: fixed; top: 0; left: 0; bottom: 0;
          width: 272px; z-index: 210;
          background: linear-gradient(180deg, #07162d 0%, #0d1e3d 50%, #112040 100%);
          border-right: 1px solid rgba(255,255,255,0.05);
          box-shadow: 4px 0 32px rgba(0,0,0,0.4);
          display: flex; flex-direction: column;
          animation: slideInMenu 0.25s cubic-bezier(.22,.68,0,1.1);
        }
        .drawer-header {
          padding: 16px 14px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          display: flex; align-items: center; justify-content: space-between;
        }
        .drawer-section-title {
          font-family: ${F}; font-size: 9px; font-weight: 800;
          color: rgba(255,255,255,0.28); text-transform: uppercase;
          letter-spacing: 2px; padding: 18px 16px 8px;
        }
        .drawer-link {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 14px; margin: 0 8px;
          border-radius: 10px;
          border: 1px solid transparent;
          font-family: ${F}; font-size: 14px; font-weight: 600;
          color: rgba(255,255,255,0.75);
          text-decoration: none; cursor: pointer;
          background: none; width: calc(100% - 16px); text-align: left;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .drawer-link:hover {
          background: rgba(255,255,255,0.07);
          border-color: rgba(255,255,255,0.1);
          color: white;
        }
        .drawer-footer {
          margin-top: auto;
          padding: 14px 12px;
          border-top: 1px solid rgba(255,255,255,0.07);
          display: flex; flex-direction: column; gap: 8px;
        }
        .btn-drawer-login {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px; border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.06);
          font-family: ${F}; font-size: 14px; font-weight: 700;
          color: rgba(255,255,255,0.8); cursor: pointer;
          transition: background 0.15s;
        }
        .btn-drawer-login:hover { background: rgba(255,255,255,0.12); }
        .btn-drawer-signup {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 13px; border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #173b78, #2a66b8);
          font-family: ${F}; font-size: 14px; font-weight: 800;
          color: white; cursor: pointer;
          box-shadow: 0 6px 20px rgba(23,59,120,0.5);
          transition: opacity 0.15s;
        }
        .btn-drawer-signup:hover { opacity: 0.88; }
      `}</style>

      {/* Barre fixe */}
      <nav className="nav-root">
        {/* Hamburger gauche */}
        <button className="nav-icon-btn" onClick={() => setOpen(true)}>
          <Menu size={18} />
        </button>

        {/* Logo centré */}
        <img
          src="/logo-wakaana.png"
          alt="Wakaana Express"
          style={{ height: 68, maxWidth: 160, objectFit: 'contain', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
          onError={e => { e.target.src = '/logo-white.png'; }}
        />

        {/* Spacer droit (équilibre) */}
        <div style={{ width: 36 }} />
      </nav>

      {/* Drawer menu */}
      {open && (
        <>
          <div className="nav-overlay" onClick={() => setOpen(false)} />
          <div className="nav-drawer">
            {/* En-tête drawer */}
            <div className="drawer-header">
              <img
                src="/logo-wakaana.png"
                alt="Wakaana Express"
                style={{ height: 52, objectFit: 'contain' }}
                onError={e => { e.target.src = '/logo-white.png'; }}
              />
              <button className="nav-icon-btn" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>

            {/* Liens navigation */}
            <div className="drawer-section-title">Navigation</div>
            {navLinks.map((l, i) => (
              <a
                key={i}
                href={l.href}
                className="drawer-link"
                onClick={() => setOpen(false)}
              >
                {l.label}
                <ChevronRight size={14} color="rgba(255,255,255,0.3)" />
              </a>
            ))}

            {/* Footer drawer */}
            <div className="drawer-footer">
              <button className="btn-drawer-login" onClick={() => { setOpen(false); onLogin(); }}>
                <LogIn size={16} />
                Se connecter
              </button>
              <button className="btn-drawer-signup" onClick={() => { setOpen(false); onSignup(); }}>
                <UserPlus size={16} />
                S'inscrire gratuitement
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ onSignup }) {
  return (
    <>
      <style>{`
        @keyframes pulse1  { 0%,100%{transform:scale(1) translate(0,0);} 50%{transform:scale(1.07) translate(14px,-18px);} }
        @keyframes pulse2  { 0%,100%{transform:scale(1) translate(0,0);} 50%{transform:scale(1.05) translate(-12px,16px);} }
        @keyframes pulse3  { 0%,100%{transform:scale(1);} 50%{transform:scale(1.09);} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(24px);} to{opacity:1;transform:translateY(0);} }
        @keyframes fadeUp2 { from{opacity:0;transform:translateY(18px);} to{opacity:1;transform:translateY(0);} }
        .hero-root {
          position: relative; overflow: hidden;
          min-height: 100vh;
          background: #080e1c;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center;
          padding: 120px 24px 80px;
        }
        /* Photo de fond — découpe de la bannière limitée au livreur.
           Le panneau texte navy est retiré du fichier pour ne jamais
           entrer en concurrence avec le titre du site. */
        .hero-photo {
          position: absolute; inset: 0;
          background-image: url('/images/hero-livreur.webp');
          background-size: cover;
          background-position: center 35%;
          pointer-events: none;
        }
        .hero-veil {
          position: absolute; inset: 0;
          background:
            linear-gradient(180deg, rgba(8,14,28,0.78) 0%, rgba(8,14,28,0.7) 40%, rgba(8,14,28,0.97) 100%),
            linear-gradient(90deg, rgba(8,14,28,0.9) 0%, rgba(8,14,28,0.45) 55%, rgba(8,14,28,0.6) 100%);
          pointer-events: none;
        }
        @media (max-width: 700px) {
          .hero-photo { background-position: center 28%; }
        }
        .hero-orb1 {
          position:absolute; width:640px; height:640px; border-radius:50%;
          background: radial-gradient(circle, rgba(23,59,120,0.5) 0%, transparent 65%);
          top:-200px; left:-200px;
          animation: pulse1 10s ease-in-out infinite; pointer-events:none;
        }
        .hero-orb2 {
          position:absolute; width:500px; height:500px; border-radius:50%;
          background: radial-gradient(circle, rgba(197,58,45,0.18) 0%, transparent 65%);
          bottom:-160px; right:-160px;
          animation: pulse2 13s ease-in-out infinite; pointer-events:none;
        }
        .hero-orb3 {
          position:absolute; width:300px; height:300px; border-radius:50%;
          background: radial-gradient(circle, rgba(42,102,184,0.15) 0%, transparent 65%);
          top:40%; right:5%;
          animation: pulse3 8s ease-in-out infinite; pointer-events:none;
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 50px; padding: 7px 16px;
          margin-bottom: 28px;
          animation: fadeUp 0.5s ease;
        }
        .hero-dot {
          width: 7px; height: 7px; border-radius: 50%; background: #4ade80;
          box-shadow: 0 0 8px #4ade80;
        }
        .hero-badge-text {
          font-family: ${F}; font-size: 12px; font-weight: 600;
          color: rgba(255,255,255,0.75); letter-spacing: 0.3px;
        }
        .hero-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(38px, 6vw, 68px);
          color: white; line-height: 1.08;
          letter-spacing: -1.5px;
          margin: 0 0 22px;
          animation: fadeUp 0.55s ease;
        }
        .hero-title span { color: #c53a2d; }
        .hero-sub {
          font-family: ${F}; font-size: clamp(15px, 2vw, 18px);
          color: rgba(255,255,255,0.55);
          line-height: 1.75; max-width: 560px; margin: 0 auto 40px;
          animation: fadeUp2 0.65s ease;
        }
        .hero-actions {
          display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
          animation: fadeUp2 0.7s ease;
        }
        .btn-hero-main {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #173b78 0%, #2a66b8 100%);
          color: white; padding: 15px 28px; border-radius: 13px;
          font-family: ${F}; font-weight: 800; font-size: 15px;
          border: none; cursor: pointer;
          box-shadow: 0 10px 32px rgba(23,59,120,0.55);
          transition: opacity 0.15s, transform 0.15s;
        }
        .btn-hero-main:hover { opacity: 0.88; transform: translateY(-2px); }
        .btn-hero-ghost {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          color: rgba(255,255,255,0.8); padding: 15px 26px; border-radius: 13px;
          font-family: ${F}; font-weight: 700; font-size: 15px;
          cursor: pointer; text-decoration: none;
          transition: background 0.15s, border-color 0.15s;
        }
        .btn-hero-ghost:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.25); }
        .hero-stats {
          display: flex; gap: 40px; justify-content: center;
          margin-top: 64px; flex-wrap: wrap;
          animation: fadeUp2 0.8s ease;
        }
        .hero-stat-val {
          font-family: ${F}; font-weight: 900; font-size: 30px;
          color: white; line-height: 1;
        }
        .hero-stat-unit { font-size: 14px; color: rgba(255,255,255,0.4); margin-left: 3px; font-weight: 600; }
        .hero-stat-label { font-family: ${F}; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4); margin-top: 5px; text-transform: uppercase; letter-spacing: 0.8px; }
        .hero-divider { width: 1px; background: rgba(255,255,255,0.1); align-self: stretch; }
      `}</style>

      <section className="hero-root">
        <div className="hero-photo" />
        <div className="hero-veil" />
        <div className="hero-orb1" />
        <div className="hero-orb2" />
        <div className="hero-orb3" />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 760 }}>
          <div className="hero-badge">
            <span className="hero-dot" />
            <span className="hero-badge-text">Partenaire logistique des e-commerçants au Sénégal</span>
          </div>

          <h1 className="hero-title">
            Vendez plus.<br />
            On <span>livre</span> le reste.
          </h1>

          <p className="hero-sub">
            Wakaana Express prend en charge la logistique complète de votre e-commerce —
            gestion de stock, confirmation client et livraison express.
            Concentrez-vous sur la vente, on s'occupe du reste.
          </p>

          <div className="hero-actions">
            <button className="btn-hero-main" onClick={onSignup}>
              Démarrer maintenant <ArrowRight size={17} strokeWidth={2.5} />
            </button>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="btn-hero-ghost">
              <WaIcon size={17} color="rgba(255,255,255,0.7)" />
              Nous contacter
            </a>
          </div>

          <div className="hero-stats">
            {[
              { val: '2 000', unit: 'FCFA', label: 'par livraison' },
              { val: '7j/7', unit: '', label: 'disponible' },
              { val: '100%', unit: '', label: 'suivi GPS' },
              { val: '3', unit: '', label: 'services en 1' },
            ].map((s, i, arr) => (
              <Fragment key={s.label}>
                <div style={{ textAlign: 'center' }}>
                  <div className="hero-stat-val">
                    {s.val}<span className="hero-stat-unit">{s.unit}</span>
                  </div>
                  <div className="hero-stat-label">{s.label}</div>
                </div>
                {i < arr.length - 1 && <div className="hero-divider" />}
              </Fragment>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Section Services ─────────────────────────────────────────────────────────
function Services() {
  return (
    <>
      <style>{`
        .svc-root {
          background: #080e1c;
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 100px 24px;
        }
        .svc-inner { max-width: 1120px; margin: 0 auto; }
        .svc-label {
          font-family: ${F}; font-size: 11px; font-weight: 800;
          color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 2px;
          margin-bottom: 16px;
        }
        .svc-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(28px,4vw,44px);
          color: white; line-height: 1.1; letter-spacing: -0.8px;
          margin: 0 0 16px;
        }
        .svc-sub {
          font-family: ${F}; font-size: 15px; color: rgba(255,255,255,0.45);
          max-width: 480px; line-height: 1.7; margin: 0;
        }
        .svc-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px; margin-top: 60px;
        }
        .svc-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 20px; overflow: hidden;
          display: flex; flex-direction: column;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
          cursor: default;
        }
        .svc-card:hover {
          background: rgba(255,255,255,0.07);
          border-color: rgba(255,255,255,0.15);
          transform: translateY(-3px);
        }
        .svc-photo-wrap { position: relative; line-height: 0; }
        .svc-photo {
          width: 100%; height: 200px;
          object-fit: cover; display: block;
        }
        /* Fondu vers le fond de la carte pour souder photo et texte */
        .svc-photo-fade {
          position: absolute; left: 0; right: 0; bottom: 0; height: 70px;
          background: linear-gradient(180deg, rgba(8,14,28,0) 0%, rgba(11,17,32,0.92) 100%);
          pointer-events: none;
        }
        .svc-body { padding: 0 28px 30px; }
        .svc-icon-wrap {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(23,59,120,0.85);
          border: 1px solid rgba(42,102,184,0.45);
          display: flex; align-items: center; justify-content: center;
          margin: -26px 0 20px; position: relative; z-index: 1;
          box-shadow: 0 8px 20px rgba(4,10,22,0.55);
        }
        .svc-card-title {
          font-family: ${F}; font-weight: 800; font-size: 18px;
          color: white; margin: 0 0 12px;
        }
        .svc-card-desc {
          font-family: ${F}; font-size: 14px;
          color: rgba(255,255,255,0.5); line-height: 1.7; margin: 0 0 20px;
        }
        .svc-tags { display: flex; flex-wrap: wrap; gap: 6px; }
        .svc-tag {
          font-family: ${F}; font-size: 11px; font-weight: 600;
          color: rgba(255,255,255,0.45);
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.09);
          padding: 4px 10px; border-radius: 50px;
        }
      `}</style>

      <section id="services" className="svc-root">
        <div className="svc-inner">
          <div className="svc-label">Ce qu'on fait</div>
          <h2 className="svc-title">
            Une logistique complète<br />pour votre boutique en ligne
          </h2>
          <p className="svc-sub">
            Du stockage à la livraison, en passant par la confirmation client — Wakaana Express
            prend en charge tout ce qui vous fait perdre du temps.
          </p>

          <div className="svc-grid">
            {SERVICES.map((s, i) => (
              <div key={i} className="svc-card">
                <div className="svc-photo-wrap">
                  <img
                    className="svc-photo"
                    src={s.img}
                    alt={s.imgAlt}
                    loading="lazy"
                    style={{ objectPosition: s.imgPos }}
                  />
                  <div className="svc-photo-fade" />
                </div>
                <div className="svc-body">
                  <div className="svc-icon-wrap"><s.Icon size={22} color="#bfdbfe" strokeWidth={1.8} /></div>
                  <h3 className="svc-card-title">{s.title}</h3>
                  <p className="svc-card-desc">{s.desc}</p>
                  <div className="svc-tags">
                    {s.tags.map((t, j) => <span key={j} className="svc-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Comment ça marche ────────────────────────────────────────────────────────
function HowItWorks({ onSignup }) {
  return (
    <>
      <style>{`
        .how-root {
          background: #07111f;
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 100px 24px;
        }
        .how-inner { max-width: 1120px; margin: 0 auto; }
        .how-label {
          font-family: ${F}; font-size: 11px; font-weight: 800;
          color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 2px;
          margin-bottom: 16px;
        }
        .how-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(28px,4vw,44px);
          color: white; letter-spacing: -0.8px; margin: 0 0 60px;
        }
        .how-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 2px; position: relative;
        }
        .how-step {
          padding: 36px 32px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 18px;
          position: relative; overflow: hidden;
        }
        .how-step::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, #173b78, #2a66b8);
          opacity: 0; transition: opacity 0.2s;
        }
        .how-step:hover::before { opacity: 1; }
        .how-num {
          font-family: ${F}; font-size: 42px; font-weight: 900;
          color: rgba(255,255,255,0.07); line-height: 1;
          margin-bottom: 20px; letter-spacing: -2px;
        }
        .how-step-title {
          font-family: ${F}; font-weight: 800; font-size: 17px;
          color: white; margin: 0 0 12px;
        }
        .how-step-desc {
          font-family: ${F}; font-size: 14px;
          color: rgba(255,255,255,0.45); line-height: 1.7; margin: 0;
        }
        .how-visual {
          margin-top: 56px; border-radius: 20px; overflow: hidden;
          border: 1px solid rgba(255,255,255,0.08);
          position: relative; line-height: 0;
        }
        .how-visual img {
          width: 100%; height: 360px; display: block;
          object-fit: cover; object-position: center 42%;
        }
        .how-visual-cap {
          position: absolute; left: 0; right: 0; bottom: 0;
          padding: 46px 26px 22px;
          background: linear-gradient(180deg, rgba(7,17,31,0) 0%, rgba(7,17,31,0.95) 72%);
          font-family: ${F}; font-size: 14px; font-weight: 600;
          color: rgba(255,255,255,0.88); line-height: 1.55;
        }
        @media (max-width: 700px) {
          .how-visual img { height: 260px; }
        }
        .how-cta { text-align: center; margin-top: 56px; }
        .btn-how {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #173b78, #2a66b8);
          color: white; padding: 14px 28px; border-radius: 13px;
          font-family: ${F}; font-weight: 800; font-size: 15px;
          border: none; cursor: pointer;
          box-shadow: 0 8px 28px rgba(23,59,120,0.5);
          transition: opacity 0.15s, transform 0.15s;
        }
        .btn-how:hover { opacity: 0.88; transform: translateY(-2px); }
      `}</style>

      <section id="comment" className="how-root">
        <div className="how-inner">
          <div className="how-label">Démarrage simple</div>
          <h2 className="how-title">Prêt en quelques minutes</h2>

          <div className="how-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="how-step">
                <div className="how-num">{s.num}</div>
                <h3 className="how-step-title">{s.title}</h3>
                <p className="how-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="how-visual">
            <img
              src="/images/suivi-temps-reel.webp"
              alt="Suivi d'une commande en temps réel sur téléphone et tableau de bord Wakaana Express"
              loading="lazy"
            />
            <div className="how-visual-cap">
              Chaque commande suivie en direct — sur votre téléphone comme sur votre tableau de bord.
            </div>
          </div>

          <div className="how-cta">
            <button className="btn-how" onClick={onSignup}>
              Créer mon compte gratuitement →
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Tarifs ───────────────────────────────────────────────────────────────────
function Tarifs({ onSignup }) {
  return (
    <>
      <style>{`
        .tarif-root {
          background: #080e1c;
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 100px 24px;
        }
        .tarif-inner { max-width: 1120px; margin: 0 auto; }
        .tarif-label {
          font-family: ${F}; font-size: 11px; font-weight: 800;
          color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 2px;
          margin-bottom: 16px;
        }
        .tarif-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(28px,4vw,44px);
          color: white; letter-spacing: -0.8px;
          margin: 0 0 12px;
        }
        .tarif-sub {
          font-family: ${F}; font-size: 15px; color: rgba(255,255,255,0.4);
          margin: 0 0 60px; max-width: 440px; line-height: 1.65;
        }
        .tarif-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px; max-width: 760px;
        }
        .tarif-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px; padding: 36px 32px;
          position: relative; overflow: hidden;
        }
        .tarif-card.featured {
          background: rgba(23,59,120,0.25);
          border-color: rgba(42,102,184,0.5);
          box-shadow: 0 0 0 1px rgba(42,102,184,0.2), 0 20px 60px rgba(23,59,120,0.2);
        }
        .tarif-badge {
          display: inline-block;
          font-family: ${F}; font-size: 10px; font-weight: 800;
          color: #93c5fd; background: rgba(42,102,184,0.3);
          border: 1px solid rgba(42,102,184,0.4);
          padding: 4px 10px; border-radius: 50px;
          margin-bottom: 20px; letter-spacing: 1px; text-transform: uppercase;
        }
        .tarif-zone {
          font-family: ${F}; font-size: 12px; font-weight: 700;
          color: rgba(255,255,255,0.4); text-transform: uppercase;
          letter-spacing: 1px; margin-bottom: 12px;
        }
        .tarif-price {
          font-family: ${F}; font-weight: 900;
          font-size: 52px; color: white; line-height: 1;
          margin-bottom: 4px; letter-spacing: -2px;
        }
        .tarif-currency {
          font-size: 18px; color: rgba(255,255,255,0.5);
          margin-left: 4px; font-weight: 700; letter-spacing: 0;
        }
        .tarif-per {
          font-family: ${F}; font-size: 13px;
          color: rgba(255,255,255,0.35); margin-bottom: 28px;
        }
        .tarif-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
        .tarif-feature {
          display: flex; align-items: center; gap: 10px;
          font-family: ${F}; font-size: 13px; color: rgba(255,255,255,0.6);
        }
        .tarif-check {
          width: 18px; height: 18px; border-radius: 50%;
          background: rgba(42,102,184,0.25);
          border: 1px solid rgba(42,102,184,0.4);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .tarif-note {
          font-family: ${F}; font-size: 12px; color: rgba(255,255,255,0.25);
          margin-top: 40px; line-height: 1.6;
        }
        .tarif-cta { margin-top: 40px; }
        .btn-tarif {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #173b78, #2a66b8);
          color: white; padding: 13px 24px; border-radius: 11px;
          font-family: ${F}; font-weight: 700; font-size: 14px;
          border: none; cursor: pointer;
          box-shadow: 0 6px 20px rgba(23,59,120,0.45);
          transition: opacity 0.15s, transform 0.15s;
        }
        .btn-tarif:hover { opacity: 0.88; transform: translateY(-1px); }
      `}</style>

      <section id="tarifs" className="tarif-root">
        <div className="tarif-inner">
          <div className="tarif-label">Tarification</div>
          <h2 className="tarif-title">Clair, simple, sans surprise</h2>
          <p className="tarif-sub">
            Un tarif unique par livraison. Aucun abonnement, aucun frais caché.
            Vous payez uniquement ce qui est livré.
          </p>

          <div className="tarif-grid">
            {/* Dakar */}
            <div className="tarif-card featured">
              <div className="tarif-badge">✦ Tarif standard</div>
              <div className="tarif-zone" style={{ display:'flex', alignItems:'center', gap:6 }}><Truck size={13} strokeWidth={2} style={{opacity:0.6}}/>Dakar & périphérie</div>
              <div className="tarif-price">
                2 000<span className="tarif-currency">FCFA</span>
              </div>
              <div className="tarif-per">par livraison effectuée, partout à Dakar</div>
              <ul className="tarif-features">
                {[
                  'Confirmation client incluse',
                  'Suivi GPS en temps réel',
                  'Notification de livraison',
                  'Gestion des retours',
                  'Tableau de bord marchand',
                ].map((f, i) => (
                  <li key={i} className="tarif-feature">
                    <span className="tarif-check"><CheckCircle size={11} color="#93c5fd" strokeWidth={2.5} /></span> {f}
                  </li>
                ))}
              </ul>
              <div className="tarif-cta">
                <button className="btn-tarif" onClick={onSignup}>
                  Commencer maintenant →
                </button>
              </div>
            </div>

            {/* Hors Dakar */}
            <div className="tarif-card">
              <div className="tarif-zone" style={{ display:'flex', alignItems:'center', gap:6 }}><Layers size={13} strokeWidth={2} style={{opacity:0.6}}/>Hors de Dakar</div>
              <div className="tarif-price">
                2 000<span className="tarif-currency">FCFA</span>
              </div>
              <div className="tarif-per">votre tarif reste le même qu'à Dakar</div>
              <ul className="tarif-features">
                {[
                  'Partout au Sénégal',
                  '+ 2 000 FCFA de transport réglés par le client',
                  'Confirmés avant expédition',
                  'Même niveau de suivi',
                ].map((f, i) => (
                  <li key={i} className="tarif-feature">
                    <span className="tarif-check"><CheckCircle size={11} color="#93c5fd" strokeWidth={2.5} /></span> {f}
                  </li>
                ))}
              </ul>
              <p className="tarif-note">
                Hors de Dakar, le transport de 2 000 FCFA est payé par le client final à la livraison. Vous, vous ne payez que vos 2 000 FCFA habituels.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Pourquoi nous ────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { Icon: Zap,         title: 'Réactif',      desc: 'Nos équipes traitent vos commandes rapidement. Moins d\'attente, plus de livraisons.' },
    { Icon: BarChart2,   title: 'Transparent',   desc: 'Suivez chaque commande en temps réel depuis votre tableau de bord marchand.' },
    { Icon: Layers,      title: 'Intégré',       desc: 'Compatible Shopify et Google Sheets. Import automatique de vos commandes.' },
    { Icon: ShoppingBag, title: 'Dédié e-com',   desc: 'Conçu pour les vendeurs en ligne. On comprend vos contraintes business.' },
  ];
  return (
    <>
      <style>{`
        .why-root {
          position: relative; overflow: hidden;
          background: #07111f;
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 100px 24px;
        }
        /* Poignée de main marchand / agent — en fond très voilé,
           elle porte l'idée de confiance sans gêner la lecture */
        .why-photo {
          position: absolute; inset: 0;
          background-image: url('/images/partenariat.webp');
          background-size: cover;
          background-position: center 30%;
          pointer-events: none;
        }
        .why-veil {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(7,17,31,0.94) 0%, rgba(7,17,31,0.88) 50%, rgba(7,17,31,0.96) 100%);
          pointer-events: none;
        }
        .why-inner { position: relative; z-index: 1; max-width: 1120px; margin: 0 auto; }
        .why-label {
          font-family: ${F}; font-size: 11px; font-weight: 800;
          color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 2px;
          margin-bottom: 16px;
        }
        .why-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(28px,4vw,44px);
          color: white; letter-spacing: -0.8px;
          margin: 0 0 56px;
        }
        .why-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 14px;
        }
        .why-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px; padding: 28px 24px;
          display: flex; flex-direction: column; gap: 14px;
          transition: background 0.2s, border-color 0.2s;
        }
        .why-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.12);
        }
        .why-icon-wrap {
          width: 44px; height: 44px; border-radius: 12px;
          background: rgba(23,59,120,0.25);
          border: 1px solid rgba(42,102,184,0.3);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .why-card-title {
          font-family: ${F}; font-weight: 800; font-size: 16px;
          color: white; margin: 0;
        }
        .why-card-desc {
          font-family: ${F}; font-size: 13px;
          color: rgba(255,255,255,0.42); line-height: 1.65; margin: 0;
        }
      `}</style>

      <section className="why-root">
        <div className="why-photo" />
        <div className="why-veil" />
        <div className="why-inner">
          <div className="why-label">Nos engagements</div>
          <h2 className="why-title">Pourquoi nous choisir ?</h2>
          <div className="why-grid">
            {points.map((p, i) => (
              <div key={i} className="why-card">
                <div className="why-icon-wrap">
                  <p.Icon size={19} color="#93c5fd" strokeWidth={1.8} />
                </div>
                <h3 className="why-card-title">{p.title}</h3>
                <p className="why-card-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─── CTA Final ────────────────────────────────────────────────────────────────
function CtaFinal({ onSignup }) {
  return (
    <>
      <style>{`
        .cta-root {
          background: #080e1c;
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 100px 24px;
          text-align: center;
        }
        .cta-box {
          max-width: 680px; margin: 0 auto;
          background: rgba(23,59,120,0.2);
          border: 1px solid rgba(42,102,184,0.3);
          border-radius: 28px; padding: 64px 40px;
          position: relative; overflow: hidden;
        }
        .cta-orb {
          position: absolute; width: 300px; height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(23,59,120,0.4) 0%, transparent 65%);
          top: -80px; right: -80px; pointer-events: none;
        }
        .cta-title {
          font-family: ${F}; font-weight: 900;
          font-size: clamp(26px,4vw,40px);
          color: white; letter-spacing: -0.8px;
          margin: 0 0 16px; position: relative; z-index: 1;
        }
        .cta-sub {
          font-family: ${F}; font-size: 15px;
          color: rgba(255,255,255,0.5); line-height: 1.7;
          margin: 0 0 36px; max-width: 440px; margin-left: auto; margin-right: auto;
          position: relative; z-index: 1;
        }
        .cta-actions {
          display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
          position: relative; z-index: 1;
        }
        .btn-cta-main {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #173b78, #2a66b8);
          color: white; padding: 15px 28px; border-radius: 13px;
          font-family: ${F}; font-weight: 800; font-size: 15px;
          border: none; cursor: pointer;
          box-shadow: 0 10px 32px rgba(23,59,120,0.6);
          transition: opacity 0.15s, transform 0.15s;
        }
        .btn-cta-main:hover { opacity: 0.88; transform: translateY(-2px); }
        .btn-cta-wa {
          display: inline-flex; align-items: center; gap: 8px;
          background: #25D366; color: white;
          padding: 15px 24px; border-radius: 13px;
          font-family: ${F}; font-weight: 700; font-size: 15px;
          text-decoration: none;
          box-shadow: 0 6px 20px rgba(37,211,102,0.3);
          transition: opacity 0.15s, transform 0.15s;
        }
        .btn-cta-wa:hover { opacity: 0.88; transform: translateY(-2px); }
      `}</style>

      <section className="cta-root">
        <div className="cta-box">
          <div className="cta-orb" />
          <h2 className="cta-title">Prêt à déléguer votre logistique ?</h2>
          <p className="cta-sub">
            Rejoignez les e-commerçants qui font confiance à Wakaana Express
            pour gérer leur stock, leurs clients et leurs livraisons.
          </p>
          <div className="cta-actions">
            <button className="btn-cta-main" onClick={onSignup}>
              <UserPlus size={17} strokeWidth={2} />
              Créer mon compte
            </button>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="btn-cta-wa">
              <WaIcon size={17} color="white" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer({ onLogin }) {
  return (
    <>
      <style>{`
        .footer-root {
          background: #040a14;
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 56px 24px 32px;
        }
        .footer-inner {
          max-width: 1120px; margin: 0 auto;
        }
        .footer-top {
          display: flex; flex-wrap: wrap; gap: 48px;
          justify-content: space-between; margin-bottom: 48px;
        }
        .footer-brand { max-width: 280px; }
        .footer-desc {
          font-family: ${F}; font-size: 13px;
          color: rgba(255,255,255,0.3); line-height: 1.7;
          margin: 16px 0 0;
        }
        .footer-col-title {
          font-family: ${F}; font-size: 11px; font-weight: 800;
          color: rgba(255,255,255,0.3); text-transform: uppercase;
          letter-spacing: 1.5px; margin-bottom: 18px;
        }
        .footer-link {
          display: block;
          font-family: ${F}; font-size: 13px; font-weight: 500;
          color: rgba(255,255,255,0.45); text-decoration: none;
          margin-bottom: 12px; transition: color 0.15s; cursor: pointer;
          background: none; border: none; text-align: left; padding: 0;
        }
        .footer-link:hover { color: rgba(255,255,255,0.8); }
        .footer-bottom {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding-top: 24px;
          display: flex; flex-wrap: wrap; gap: 12px;
          align-items: center; justify-content: space-between;
        }
        .footer-copy {
          font-family: ${F}; font-size: 12px;
          color: rgba(255,255,255,0.2);
        }
        .footer-team {
          font-family: ${F}; font-size: 12px;
          color: rgba(255,255,255,0.2); cursor: pointer;
          background: none; border: none; padding: 0;
          transition: color 0.15s;
        }
        .footer-team:hover { color: rgba(255,255,255,0.45); }
        .btn-wa-footer {
          display: inline-flex; align-items: center; gap: 7px;
          background: rgba(37,211,102,0.12);
          border: 1px solid rgba(37,211,102,0.25);
          color: #4ade80; padding: 9px 16px; border-radius: 9px;
          font-family: ${F}; font-weight: 700; font-size: 13px;
          text-decoration: none; transition: background 0.15s;
        }
        .btn-wa-footer:hover { background: rgba(37,211,102,0.2); }
      `}</style>

      <footer className="footer-root">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <img
                src="/logo-white.png"
                alt="Wakaana Express"
                style={{ height: 30, objectFit: 'contain' }}
                onError={e => { e.target.src = '/logo-transparent.png'; }}
              />
              <p className="footer-desc">
                Partenaire logistique des e-commerçants au Sénégal.
                Stock, closing client et livraison express.
              </p>
            </div>

            <div>
              <div className="footer-col-title">Navigation</div>
              <a href="#services" className="footer-link">Nos services</a>
              <a href="#comment" className="footer-link">Comment ça marche</a>
              <a href="#tarifs" className="footer-link">Tarifs</a>
              <a href="/recrutement" className="footer-link">Rejoindre l'équipe</a>
              <a href="/track" className="footer-link">Suivi de commande</a>
            </div>

            <div>
              <div className="footer-col-title">Contact</div>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="btn-wa-footer">
                <WaIcon size={15} color="#4ade80" />
                WhatsApp Business
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <span className="footer-copy">© 2025 Wakaana Express. Tous droits réservés.</span>
            <button className="footer-team" onClick={onLogin}>Espace équipe →</button>
          </div>
        </div>
      </footer>
    </>
  );
}

// ─── Bouton flottant WhatsApp ─────────────────────────────────────────────────
function FloatingWA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);
  return (
    <a
      href={WA_LINK}
      target="_blank"
      rel="noopener noreferrer"
      title="Nous contacter sur WhatsApp"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 200,
        width: 54, height: 54, borderRadius: '50%',
        background: '#25D366',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 28px rgba(37,211,102,0.45)',
        textDecoration: 'none',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.6)',
        transition: 'opacity 0.4s, transform 0.4s',
      }}
    >
      <WaIcon size={26} color="white" />
    </a>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const onSignup = () => navigate(APP_ROUTES.register);
  const onLogin  = () => navigate(APP_ROUTES.home);

  return (
    <div style={{ background: '#080e1c', overflowX: 'hidden' }}>
      <Navbar onLogin={onLogin} onSignup={onSignup} />
      <Hero onSignup={onSignup} />
      <Services />
      <HowItWorks onSignup={onSignup} />
      <Tarifs onSignup={onSignup} />
      <WhyUs />
      <CtaFinal onSignup={onSignup} />
      <Footer onLogin={onLogin} />
      <FloatingWA />
    </div>
  );
}
